<template>
  <v-card class="edit-purchase-order" height="90vh">
    <v-card-title>{{ $t('admin_acc_purchase_order.edit_title') }}</v-card-title>
    <v-card-text>
      <div class="edit-purchase-order-content" v-if="purchaseOrder">
        <div class="edit-purchase-order-content-col left">
          <v-form ref="form">
            <v-text-field readonly
                          :label="$t('admin_acc_purchase_order.id')"
                          :value="`PO${purchaseOrder.id.toString().padStart(10, '0')}`"></v-text-field>
            <v-text-field readonly
                          :label="$t('admin_acc_purchase_order.status')"
                          v-model="purchaseOrder.status"></v-text-field>
            <v-input :rules="requiredRules"
                     v-model="purchaseOrder.issued_at">
              <v-container fluid class="flex-column pa-0">
                <legend class="v-label theme--light">{{ $t('admin_acc_purchase_order.issued_at') }}</legend>
                <DatePickerInput v-model="purchaseOrder.issued_at"
                                 hide-details></DatePickerInput>
              </v-container>
            </v-input>
            <v-input :rules="requiredRules"
                     v-model="purchaseOrder.due_at">
              <v-container fluid class="flex-column pa-0">
                <legend class="v-label theme--light">{{ $t('admin_acc_purchase_order.due_at') }}</legend>
                <DatePickerInput v-model="purchaseOrder.due_at"
                                 hide-details></DatePickerInput>
              </v-container>
            </v-input>
            <v-text-field readonly
                          :label="$t('admin_acc_purchase_order.recipient')"
                          :value="$t('name', {given_name: purchaseOrder.recipient.given_name, surname: purchaseOrder.recipient.surname})"></v-text-field>
            <v-text-field outlined
                          :label="$t('admin_acc_purchase_order.title')"
                          v-model="purchaseOrder.title"
                          :rules="requiredRules"></v-text-field>
            <v-text-field outlined
                          :label="$t('admin_acc_purchase_order.amount')"
                          type="number"
                          step=".01"
                          min="0"
                          prefix="$"
                          v-model="purchaseOrder.amount"></v-text-field>
            <v-text-field readonly
                          :label="$t('admin_acc_purchase_order.paid')"
                          type="number"
                          step=".01"
                          min="0"
                          prefix="$"
                          :value="purchaseOrder.paid"></v-text-field>
            <v-text-field readonly
                          :label="$t('admin_acc_purchase_order.due')"
                          type="number"
                          step=".01"
                          min="0"
                          prefix="$"
                          :value="(purchaseOrder.amount - purchaseOrder.paid).toFixed(2)"></v-text-field>
            <v-textarea outlined
                        :label="$t('admin_acc_purchase_order.remarks')"
                        v-model="purchaseOrder.remarks"></v-textarea>
          </v-form>
        </div>
        <div class="edit-purchase-order-content-col right">
          <v-tabs v-model="tabIndex"
                  class="flex-grow-0">
            <v-tab>{{ $t('admin_acc_purchase_order.bookings') }}</v-tab>
            <v-tab>{{ $t('admin_acc_purchase_order.invoices') }}</v-tab>
          </v-tabs>
          <v-tabs-items v-model="tabIndex"
                        class="flex-grow-1">
            <v-tab-item>
              <v-row class="flex-grow-0 mt-2" no-gutters>
                <v-btn class="ml-2"
                       color="primary"
                       @click="selectedBookingIds = [...bookingIds]">{{ $t('admin_acc_purchase_order.select_all_bookings') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn class="ml-2"
                       color="primary"
                       :disabled="selectedBookingIds.length === 0"
                       @click="removeBookings">{{ $t('admin_acc_purchase_order.remove_bookings') }}
                </v-btn>
                <v-btn class="ml-2"
                       color="primary"
                       @click="addBookings">{{ $t('admin_acc_purchase_order.add_bookings') }}
                </v-btn>
                <v-btn class="ml-2"
                       color="primary"
                       @click="addRecurrentBookings">{{ $t('admin_acc_purchase_order.add_recurrent_bookings') }}
                </v-btn>
              </v-row>
              <AdminBookingList full-height
                                hide-actions
                                show-select
                                :refresh-trigger.sync="bookingRefreshTrigger"
                                v-model="selectedBookingIds"
                                :extra-filters="{'purchase_order.id': purchaseOrderId}"></AdminBookingList>
            </v-tab-item>
            <v-tab-item>
              <v-row class="flex-grow-0 mt-2" no-gutters>
                <v-spacer></v-spacer>
                <v-btn class="ml-2"
                       color="primary"
                       @click="addInvoice">{{ $t('admin_acc_invoice.add_title') }}
                </v-btn>
              </v-row>
              <AdminInvoiceList full-height
                                :refresh-trigger.sync="invoiceRefreshTrigger"
                                :extra-filters="{'purchase_order.id': purchaseOrderId}"></AdminInvoiceList>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </div>
    </v-card-text>
    <v-card-actions>
      <v-btn color="secondary"
             @click="$emit('submit')">
        {{ $t('close') }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn color="primary"
             @click="$refs.form.validate() && submit()">
        {{ $t('ok') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
import {onMounted, ref} from "@vue/composition-api";
import AdminBookingList from "@/components/acc_booking/booking/AdminBookingList.vue";
import DatePickerInput from "@/components/DatePickerInput.vue";
import SelectBookingModal from "@/components/acc_booking/booking/SelectBookingModal.vue";
import SelectRecurrentBookingModal from "@/components/acc_booking/booking/SelectRecurrentBookingModal.vue";
import AdminInvoiceList from "@/components/acc_booking/invoice/AdminInvoiceList.vue";
import AddInvoiceModal from "@/components/acc_booking/invoice/AddInvoiceModal.vue";

export default {
  name: 'EditPurchaseOrderModal',
  components: {AdminInvoiceList, DatePickerInput, AdminBookingList},
  props: {
    purchaseOrderId: [String, Number],
  },
  setup(props, {root, emit}) {
    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);
    const purchaseOrder = ref(null);
    const getPurchaseOrder = function () {
      root.$callAPI('aafb/admin/purchase_order/', {
        purchase_order_id: props.purchaseOrderId,
      }, (response) => {
        purchaseOrder.value = {
          ...response.body.purchase_order,
          issued_at: root.$parseDate(response.body.purchase_order.issued_at, 'yyyy-MM-dd'),
          due_at: root.$parseDate(response.body.purchase_order.due_at, 'yyyy-MM-dd'),
          amount: parseInt(response.body.purchase_order.amount) / 100.0,
          paid: parseInt(response.body.purchase_order.paid) / 100.0,
        };
      });
    }
    const submit = function () {
      root.$callAPI('aafb/admin/purchase_order/edit/', {
        purchase_order_id: props.purchaseOrderId,
        ...purchaseOrder.value,
        amount: Math.floor(parseFloat(purchaseOrder.value.amount) * 100),
      }, function (response) {
        emit('submit', true);
      })
    }

    onMounted(() => {
      getPurchaseOrder();
    })

    const tabIndex = ref(0);

    // Bookings
    const bookingRefreshTrigger = ref(false);
    const selectedBookingIds = ref([]);

    const removeBookings = function () {
      root.$callAPI('aafb/admin/purchase_order/remove_bookings/', {
        purchase_order_id: props.purchaseOrderId,
        booking_ids: selectedBookingIds.value,
      }, (response) => {
        bookingRefreshTrigger.value = true;
      });
    }
    const addBookings = async function () {
      const addedBookingIds = await root.$dialog.showAndWait(SelectBookingModal, {
        persistent: true,
        width: '95vw',
        height: '90vh',
      });

      if (addedBookingIds !== undefined) {
        root.$callAPI('aafb/admin/purchase_order/add_bookings/', {
          purchase_order_id: props.purchaseOrderId,
          booking_ids: addedBookingIds,
        }, (response) => {
          bookingRefreshTrigger.value = true;
        });
      }
    }

    const addRecurrentBookings = async function () {
      const bookingRecordIds = await root.$dialog.showAndWait(SelectRecurrentBookingModal, {
        persistent: true,
        width: '95vw',
        height: '90vh',
      });

      if (bookingRecordIds !== undefined) {
        root.$callAPI('aafb/admin/bookings/', {
          filters: {'booking_record.id': bookingRecordIds, 'purchase_order': false,},
          page_no: 1,
          page_size: -1,
        }, function (response) {
          const addedBookingIds = response.body.bookings.map((b) => b.id)

          if (addedBookingIds.length > 0) {
            root.$callAPI('aafb/admin/purchase_order/add_bookings/', {
              purchase_order_id: props.purchaseOrderId,
              booking_ids: addedBookingIds,
            }, (response) => {
              bookingRefreshTrigger.value = true;
            });
          }
        });
      }
    }

    // Invoices
    const invoiceRefreshTrigger = ref(false);
    const addInvoice = async function () {
      const needRefresh = await root.$dialog.showAndWait(AddInvoiceModal, {
        purchaseOrderId: purchaseOrder.value.id,
        persistent: true,
        width: '95vw',
        height: '90vh',
      });

      if (needRefresh) {
        invoiceRefreshTrigger.value = true;
      }
    }

    return {
      requiredRules,
      purchaseOrder,
      submit,

      tabIndex,

      bookingRefreshTrigger,
      selectedBookingIds,

      removeBookings,
      addBookings,
      addRecurrentBookings,

      invoiceRefreshTrigger,
      addInvoice,
    }
  }
}
</script>

<style lang="less">
.edit-purchase-order {
  display: flex !important;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .v-window__container {
    height: 100%;

    .v-window-item {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
  }

  .v-card__title, .v-card__text, .v-card__actions {
    width: 100%;
  }

  .v-card__title, .v-card__actions {
    flex: 0 0 auto;
  }

  .v-card__text {
    flex: 1 1 0;
    overflow: auto;
  }

  .edit-purchase-order-content {
    width: 100%;
    height: 100%;

    display: flex;
    gap: 12px;

    .edit-purchase-order-content-col {
      height: 100%;

      display: flex;
      flex-direction: column;
      overflow-y: auto;

      &.left {
        flex: 1 1 0;
      }

      &.right {
        flex: 2 2 0;
      }
    }
  }
}


</style>
